/**
 * Centralized query keys for React Query
 * 
 * This file contains all query keys used in the application
 * to ensure consistency and prevent duplication.
 */

import { FindAllItineraryRequest, FindAllPostRequest, StopStateEnum } from "@city-sage/core-client";

export const QueryKeys = {
    // Account related
    account: {
      base: ['account'] as const,
      details: (id?: string) => [...QueryKeys.account.base, id] as const,
      my: () => [...QueryKeys.account.base, 'my'] as const,
    },
    // Auth related
    auth: {
      base: ['auth'] as const,
      token: () => [...QueryKeys.auth.base, 'token'] as const,
    },
  // Chat related
  chat: {
    base: ['chat'] as const,
    initialMessages: (threadId?: string, itineraryId?: string) => [
      'initialMessages', 
      threadId,
      itineraryId
    ] as const,
    conversations: (accountId?: string) => [
      'conversations',
      accountId
    ] as const,
  },
    itinerary: {
        base: ['itinerary'] as const,
        all: (params: FindAllItineraryRequest = {}) => [...QueryKeys.itinerary.base, params] as const,
        byBounds: (bounds: { southWest: { lng: number; lat: number }; northEast: { lng: number; lat: number } }, params: { page?: number; limit?: number } = {}) => [...QueryKeys.itinerary.base, 'by-bounds', bounds, params] as const,
        byBoundsInfinite: (bounds: { southWest: { lng: number; lat: number }; northEast: { lng: number; lat: number } }, params: { limit?: number } = {}) => [...QueryKeys.itinerary.base, 'by-bounds-infinite', bounds, params] as const,
        details: (id: string) => [...QueryKeys.itinerary.base, id] as const,
        days: (itineraryIds: string) => [...QueryKeys.itinerary.base, itineraryIds, 'days'] as const,
        day: (itineraryId: string, dayId: string) => [...QueryKeys.itinerary.base, itineraryId, 'day', dayId] as const,
        stops: (id: string, dayId?:string, states?:StopStateEnum[]) => [...QueryKeys.itinerary.base, id, 'stops', dayId, states] as const,
        stop: (itineraryId: string, stopId: string) => [...QueryKeys.itinerary.base, itineraryId, 'stops', stopId] as const,
        legs: (itineraryId: string) => [...QueryKeys.itinerary.base, itineraryId, 'legs'] as const,
        leg: (itineraryId: string, legId: string) => [...QueryKeys.itinerary.base, itineraryId, 'legs', legId] as const,
        savedStops: () => [...QueryKeys.itinerary.base, 'saved-stops'] as const,
        savedItineraries: () => [...QueryKeys.itinerary.base, 'saved-itineraries'] as const,
        my: () => [...QueryKeys.itinerary.base, 'my'] as const,
    },

    post: {
        base: ['post'] as const,
        all: (params: FindAllPostRequest = {}) => [...QueryKeys.post.base, params] as const,
        byPostable: (postableType: string, postableId: string) => [...QueryKeys.post.base, postableType, postableId] as const,
        details: (id: string) => [...QueryKeys.post.base, id] as const,
    },

    reaction: {
        base: ['reaction'] as const,
        all: () => [...QueryKeys.reaction.base] as const,
        byReactable: (reactableType: string, reactableId?: string) => [...QueryKeys.reaction.base, reactableType, reactableId] as const,
        userReactions: (reactableType: string, reactableId: string) => [...QueryKeys.reaction.base, 'user', reactableType, reactableId] as const,
    },

    follow: {
        base: ['follow'] as const,
        all: (params = {}) => [...QueryKeys.follow.base, params] as const,
        followers: (userId: string) => [...QueryKeys.follow.base, 'followers', userId] as const,
        following: (userId: string) => [...QueryKeys.follow.base, 'following', userId] as const,
        isFollowing: (followerId: string, followingId: string) => [...QueryKeys.follow.base, 'check', followerId, followingId] as const,
        byFollower: (followerId: string) => [...QueryKeys.follow.base, 'by-follower', followerId] as const,
        byFollowing: (followingId: string) => [...QueryKeys.follow.base, 'by-following', followingId] as const,
    },

    stop: {
        base: ['stop'] as const,
        details: (id: string) => [...QueryKeys.stop.base, id] as const,
    }
  
  }; 